import { assertNever } from "@impero-com/common/utils/obj";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { diffString } from "json-diff";
import { isEqual } from "lodash-es";
import moment from "moment-timezone";

import { postControlsNextOccurrence } from "api/generated/controls/controls";
import {
    HalfYear,
    Month,
    Quarter,
    TwoMonths,
    ControlSchedulingAndSteps,
    DatabaseRecurrenceRule,
    NextOccurrence,
    ResolvedCoveredPeriod,
} from "api/generated/model";
import { legacyControlNextOccurrence } from "api/legacy/legacy-control/legacy-control";
import {
    LegacyControlControllerPostNextOccurrence,
    LegacyNextOccurrence,
} from "api/legacy/model";
import {
    LegacyControlCoveredPeriod,
    NotificationOffset as LegacyNotificationOffset,
} from "legacyTypes/control";

import { ParsedControlScheduling } from "../formData";
import { toLegacyRecurrenceAndOptions } from "../utils/legacyFormat";

export interface ParsedControlSchedulingAndSteps extends Omit<
    ControlSchedulingAndSteps,
    "scheduling"
> {
    scheduling: ParsedControlScheduling;
}

export const NEXT_OCCURRENCE_QUERY_KEY = "nextOccurrence";

export function useNextOccurrenceQuery(
    schedulingAndSteps: ParsedControlSchedulingAndSteps
) {
    return useQuery({
        queryKey: [NEXT_OCCURRENCE_QUERY_KEY, schedulingAndSteps],
        queryFn: () => nextOccurrenceQueryFn(schedulingAndSteps),
    });
}

interface NotApplicableResult {
    nextOccurrence: null;
    type: "NOT_APPLICABLE";
}
interface SuccessResult {
    nextOccurrence: NextOccurrence;
    type: "SUCCESS";
}
interface ErrorResult {
    nextOccurrence: null;
    type: "ERROR";
}
type NextOccurrenceQueryResult =
    | NotApplicableResult
    | SuccessResult
    | ErrorResult;
async function nextOccurrenceQueryFn(
    schedulingAndSteps: ParsedControlSchedulingAndSteps
): Promise<NextOccurrenceQueryResult> {
    const legacyPostNextOccurrence =
        toLegacyPostNextOccurrence(schedulingAndSteps);
    const nextOccurrenceBody: ControlSchedulingAndSteps = {
        scheduling: {
            ...schedulingAndSteps.scheduling,
            dueTime: moment(schedulingAndSteps.scheduling.dueTime).format(
                "HH:mm"
            ),
            sendTime: moment(schedulingAndSteps.scheduling.sendTime).format(
                "HH:mm"
            ),
            startDate: moment(schedulingAndSteps.scheduling.startDate).format(
                "YYYY-MM-DD"
            ),
            coveredPeriod: schedulingAndSteps.scheduling.coveredPeriod
                ? {
                      type: schedulingAndSteps.scheduling.coveredPeriod.type,
                      data: schedulingAndSteps.scheduling.coveredPeriod.data,
                  }
                : null,
        },
        stepAllocatedTimes: schedulingAndSteps.stepAllocatedTimes,
    };
    const [nextOccurrence, legacyNextOccurrence] = await Promise.all([
        postControlsNextOccurrence(nextOccurrenceBody, {
            validateStatus: (status) =>
                (status >= 200 && status < 300) || status === 404, // 404 is a valid response when there is no next occurrence.
        }),
        legacyControlNextOccurrence(
            legacyPostNextOccurrence as LegacyControlControllerPostNextOccurrence,
            {
                validateStatus: (status) =>
                    (status >= 200 && status < 300) || status === 404, // 404 is a valid response when there is no next occurrence.
            }
        ),
    ]);

    const captureSentryMessage = (message: string) => {
        Sentry.captureMessage(message, (scope) => {
            return scope.setContext("nextOccurrence", {
                legacyData: JSON.stringify(legacyPostNextOccurrence),
                scheduling: JSON.stringify(schedulingAndSteps),
                legacy: {
                    status: legacyNextOccurrence.status,
                    value:
                        legacyNextOccurrence.status === 200
                            ? JSON.stringify(legacyNextOccurrence.data)
                            : undefined,
                },
                new: {
                    status: nextOccurrence.status,
                    value:
                        nextOccurrence.status === 200
                            ? JSON.stringify(nextOccurrence.data)
                            : undefined,
                },
            });
        });
    };

    if (legacyNextOccurrence.status === 200 && nextOccurrence.status === 200) {
        const legacyNO = fromLegacyNextOccurrence(legacyNextOccurrence.data);
        const rustNO = unifyNextOccurrence(nextOccurrence.data);
        const diff = diffString(legacyNO, rustNO, { color: false });
        if (diff !== "") {
            console.debug(diff);
        }
        if (!isEqual(legacyNO, rustNO)) {
            console.error("Next occurrences differ", legacyNO, rustNO);
            captureSentryMessage("Next occurrences differ");
        } else {
            console.debug("Next occurrences are equal");
        }
        return {
            type: "SUCCESS",
            nextOccurrence: legacyNO,
        };
    } else {
        if (
            legacyNextOccurrence.status === 404 &&
            nextOccurrence.status === 404
        ) {
            console.debug("Next occurrences are equal");
            return {
                nextOccurrence: null,
                type: "NOT_APPLICABLE",
            };
        } else {
            console.error(
                "Next occurrences error(s)",
                legacyNextOccurrence.status,
                nextOccurrence.status
            );
            captureSentryMessage("Next occurrences error(s)");
        }
        return {
            nextOccurrence: null,
            type: "ERROR",
        };
    }
}

function toLegacyPostNextOccurrence(
    schedulingAndSteps: ParsedControlSchedulingAndSteps
): LegacyPostNextOccurrence {
    const { scheduling, stepAllocatedTimes } = schedulingAndSteps;

    const { recurrence, calendarId, financialYearId } =
        toLegacyRecurrenceAndOptions(scheduling);

    const legacyNextOccurrenceBody: LegacyPostNextOccurrence = {
        Recurrence: recurrence,
        NotificationOffset: {
            DaysBefore: stepAllocatedTimes[0].days, // Only days are supported for the responsible step
            Hour: scheduling.sendTime.hour(),
            Minute: scheduling.sendTime.minute(),
        },
        ReviewerOffsets: {
            FirstReviewerAllocatedTime: {
                Days: stepAllocatedTimes[1].days,
                Microseconds: stepAllocatedTimes[1].microseconds,
            },
            SecondReviewerAllocatedTime: {
                Days: stepAllocatedTimes[2].days,
                Microseconds: stepAllocatedTimes[2].microseconds,
            },
        },
        CoveredPeriod: scheduling.coveredPeriod
            ? {
                  Offset: scheduling.coveredPeriod.data,
                  Unit: scheduling.coveredPeriod.type,
              }
            : undefined,
        CalendarId:
            calendarId !== undefined && calendarId !== null
                ? calendarId
                : undefined,
        UseFinancialYear:
            financialYearId !== undefined && financialYearId !== null,
    };
    return legacyNextOccurrenceBody;
}

function fromLegacyNextOccurrence(
    legacyNextOccurrence: LegacyNextOccurrence
): NextOccurrence {
    let period: ResolvedCoveredPeriod | null = null;
    if (legacyNextOccurrence.CoveredPeriod) {
        switch (legacyNextOccurrence.CoveredPeriod.Unit) {
            case "Month": {
                period = {
                    type: "Month",
                    data: (
                        [
                            "January",
                            "February",
                            "March",
                            "April",
                            "May",
                            "June",
                            "July",
                            "August",
                            "September",
                            "October",
                            "November",
                            "December",
                        ] as Array<Month>
                    )[legacyNextOccurrence.CoveredPeriod.Value - 1],
                };
                break;
            }
            case "TwoMonths": {
                period = {
                    type: "TwoMonths",
                    data: (
                        [
                            "JanuaryFebruary",
                            "MarchApril",
                            "MayJune",
                            "JulyAugust",
                            "SeptemberOctober",
                            "NovemberDecember",
                        ] as Array<TwoMonths>
                    )[legacyNextOccurrence.CoveredPeriod.Value - 1],
                };
                break;
            }
            case "Quarter": {
                period = {
                    type: "Quarter",
                    data: (
                        ["First", "Second", "Third", "Fourth"] as Array<Quarter>
                    )[legacyNextOccurrence.CoveredPeriod.Value - 1],
                };
                break;
            }
            case "Half": {
                period = {
                    type: "Half",
                    data: (["First", "Second"] as Array<HalfYear>)[
                        legacyNextOccurrence.CoveredPeriod.Value - 1
                    ],
                };
                break;
            }
            case "Year": {
                period = {
                    type: "Year",
                    data: legacyNextOccurrence.CoveredPeriod.Value,
                };
                break;
            }
            default: {
                return assertNever(legacyNextOccurrence.CoveredPeriod.Unit);
            }
        }
    }
    const nextOccurrence: NextOccurrence = {
        assignmentOccurrences: {
            one: legacyNextOccurrence.AssignmentOccurrences.One,
            two: legacyNextOccurrence.AssignmentOccurrences.Two,
            three: legacyNextOccurrence.AssignmentOccurrences.Three,
        },
        coveredPeriod:
            period !== null && legacyNextOccurrence.CoveredPeriod
                ? {
                      range: {
                          start: moment({
                              year: legacyNextOccurrence.CoveredPeriod
                                  .StartYear,
                              month:
                                  legacyNextOccurrence.CoveredPeriod
                                      .StartMonth - 1,
                              day: legacyNextOccurrence.CoveredPeriod.StartDay,
                          }).format("YYYY-MM-DD"),
                          end: moment({
                              year: legacyNextOccurrence.CoveredPeriod.EndYear,
                              month:
                                  legacyNextOccurrence.CoveredPeriod.EndMonth -
                                  1,
                              day: legacyNextOccurrence.CoveredPeriod.EndDay,
                          }).format("YYYY-MM-DD"),
                      },
                      period,
                      financialYearDetails: legacyNextOccurrence.CoveredPeriod
                          .FinancialYearDetails
                          ? {
                                start: moment({
                                    year: legacyNextOccurrence.CoveredPeriod
                                        .FinancialYearDetails.StartYear,
                                    month:
                                        legacyNextOccurrence.CoveredPeriod
                                            .FinancialYearDetails.StartMonth -
                                        1,
                                    day: legacyNextOccurrence.CoveredPeriod
                                        .FinancialYearDetails.StartDay,
                                }).format("YYYY-MM-DD"),
                                end: moment({
                                    year: legacyNextOccurrence.CoveredPeriod
                                        .FinancialYearDetails.EndYear,
                                    month:
                                        legacyNextOccurrence.CoveredPeriod
                                            .FinancialYearDetails.EndMonth - 1,
                                    day: legacyNextOccurrence.CoveredPeriod
                                        .FinancialYearDetails.EndDay,
                                }).format("YYYY-MM-DD"),
                            }
                          : null,
                  }
                : null,
    };
    return unifyNextOccurrence(nextOccurrence);
}

// Transform UTC dates into ISO ones (only the precision changes)
function unifyNextOccurrence(nextOccurrence: NextOccurrence): NextOccurrence {
    return {
        assignmentOccurrences: {
            one: nextOccurrence.assignmentOccurrences.one.map((dateInUtc) =>
                moment(dateInUtc).toISOString(false)
            ),
            two: nextOccurrence.assignmentOccurrences.two.map((dateInUtc) =>
                moment(dateInUtc).toISOString(false)
            ),
            three: nextOccurrence.assignmentOccurrences.three.map((dateInUtc) =>
                moment(dateInUtc).toISOString(false)
            ),
        },
        coveredPeriod: nextOccurrence.coveredPeriod,
    };
}

interface LegacyPostNextOccurrence {
    Recurrence: DatabaseRecurrenceRule;
    NotificationOffset: LegacyNotificationOffset;
    ReviewerOffsets: LegacyReviewerOffsets;
    CoveredPeriod?: LegacyControlCoveredPeriod;
    CalendarId?: number;
    UseFinancialYear: boolean;
}

interface LegacyReviewerOffsets {
    FirstReviewerAllocatedTime: LegacyAllocatedTime;
    SecondReviewerAllocatedTime: LegacyAllocatedTime;
}

interface LegacyAllocatedTime {
    Days: number;
    Microseconds: number;
}
